import { Injectable } from '@angular/core';
import { ANALYTICS_EVENTS } from '../../../shared/enums/analytics-event.enum';
import { environment } from '../../../../environments/environment';

/**
 * Key the GTM container's "User ID" variable (variable 4) reads.
 * Must stay camelCase — renaming it to `user_id` breaks attribution for every event.
 * See the `userId` contract in ./CLAUDE.md.
 */
const USER_ID_KEY = 'userId';

/**
 * Hostnames mapped by the container's `Google Analytics ID` lookup variable (variable 3).
 * A hostname outside this list resolves to an undefined measurement ID, so tags fire with no
 * destination and no console error. Kept here only to make that failure discoverable in dev;
 * the container remains the source of truth. See Environment routing in ./CLAUDE.md.
 */
export const KNOWN_ANALYTICS_HOSTS: Array<string> = [
  'rms.racap.com',
  'rms-uat.racap.com',
  'rms-qa.racap.com',
  'rms-dev.racap.com',
  'localhost',
  '127.0.0.1'
];

/**
 * Owns all interaction with the Google Tag Manager data layer.
 *
 * Identity (`userId`) is a persistent data-layer value: it is pushed once when the user is known
 * and every subsequent event inherits it, which is how container GTM-NV9PKNM populates GA4's
 * reserved `user_id` field. Callers must therefore ensure `setUser` runs before any event push,
 * which is why identity is driven by AuthenticationService rather than by a component.
 *
 * This service deliberately has no constructor dependencies so that AuthenticationService can
 * inject it without risking a DI cycle.
 */
@Injectable({ providedIn: 'root' })
export class GoogleTagManagerService {

  /**
   * Last identity actually pushed, so repeated assertions of the same value do not spam the data
   * layer. `undefined` means "never pushed", which is distinct from a pushed `null`.
   */
  private lastPushedIdentity: string | null | undefined;

  constructor() {
    this.adoptSeededIdentity();
    this.warnOnUnmappedHostname();
  }

  /**
   * Associates all subsequent data-layer events with an RMS user.
   *
   * Idempotent: pushes only when the resolved identity differs from the last one pushed, so it is
   * safe to call on every navigation as a guard against ordering bugs. Pushes `null` when the id
   * is missing so the value is explicitly absent rather than stale.
   */
  public setUser(userId: number | string | null | undefined): void {
    const resolved = this.toIdentity(userId);
    if (resolved === this.lastPushedIdentity) {
      return;
    }
    this.lastPushedIdentity = resolved;
    this.logIdentity(resolved);
    this.pushOnDataLayer({ [USER_ID_KEY]: resolved });
  }

  /**
   * Removes the user association. Must be called on logout, otherwise the next user's events
   * in the same tab are attributed to the previous user (no page reload happens on logout).
   */
  public clearUser(): void {
    this.setUser(null);
  }

  /** Last identity pushed to the data layer, or undefined if identity has never been pushed. */
  public getPushedIdentity(): string | null | undefined {
    return this.lastPushedIdentity;
  }

  /** Pushes a named analytics event, dropping any parameter that is not a usable value. */
  public trackEvent(event: ANALYTICS_EVENTS, params?: Record<string, unknown>): void {
    this.pushOnDataLayer({ event, ...this.sanitize(params) });
  }

  /** Low-level escape hatch. Prefer trackEvent/setUser. */
  public pushOnDataLayer(obj: object): void {
    try {
      this.getDataLayer().push(obj);
    } catch {
      // Analytics must never break the app.
    }
  }

  private getDataLayer(): Array<any> {
    const win = window as any;
    if (!Array.isArray(win.dataLayer)) {
      win.dataLayer = [];
    }
    return win.dataLayer;
  }

  /**
   * Adopts the identity that src/index.html seeded into the data layer before gtm.js loaded, so
   * this service does not push a redundant duplicate of a value that is already in the model.
   * Leaves `lastPushedIdentity` undefined when nothing was seeded.
   */
  private adoptSeededIdentity(): void {
    try {
      const entries = this.getDataLayer();
      for (let i = entries.length - 1; i >= 0; i--) {
        const entry = entries[i];
        if (entry && typeof entry === 'object' && USER_ID_KEY in entry) {
          this.lastPushedIdentity = this.toIdentity(entry[USER_ID_KEY]);
          return;
        }
      }
    } catch {
      // An exotic or foreign dataLayer is not worth failing over; we just push our own value.
    }
  }

  /**
   * GA4 prefers a string user id; returns null only for a genuinely absent or non-numeric id.
   *
   * **`0` is a valid RMS user id** — it is the `system@racap.com` account, the same sentinel used by
   * the `created_by`/`updated_by` column defaults. Treating it as absent (a `> 0` check) makes that
   * account's traffic indistinguishable from genuinely unattributed traffic, which corrupts the very
   * null-rate metric this pipeline is measured on. RMS treats 0 as a real id elsewhere too — see
   * `crm-search.service.ts`'s `if (userId || userId === 0)`.
   *
   * The explicit absence check must come **before** coercion: `Number(null)` and `Number('')` are
   * both `0`, so coercing first would silently turn "no identity" into identity `"0"`.
   */
  private toIdentity(userId: number | string | null | undefined): string | null {
    if (userId === null || userId === undefined || userId === '') {
      return null;
    }
    const numeric = Number(userId);
    return Number.isFinite(numeric) && numeric >= 0 ? String(numeric) : null;
  }

  /** Drops NaN/undefined/null parameters so "NaN" never reaches GA4 (see finding M1). */
  private sanitize(params?: Record<string, unknown>): Record<string, unknown> {
    if (!params) {
      return {};
    }
    return Object.entries(params).reduce((acc, [key, value]) => {
      const isBadNumber = typeof value === 'number' && !Number.isFinite(value);
      if (value !== undefined && value !== null && !isBadNumber) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);
  }

  /**
   * Reports identity pushes in dev builds. Attribution failures are otherwise invisible until
   * they show up in BigQuery a day later, so make the moment identity is set observable.
   */
  private logIdentity(resolved: string | null): void {
    if (!environment.production) {
      const label = resolved === null ? 'cleared' : `set to ${resolved}`;
      console.log(`[analytics] userId ${label}`);
    }
  }

  /**
   * Warns developers when the app runs on a hostname the container does not map, because the
   * resulting data loss is otherwise completely silent. Never runs in production builds.
   */
  private warnOnUnmappedHostname(): void {
    const hostname = window?.location?.hostname;
    if (!environment.production && hostname && !KNOWN_ANALYTICS_HOSTS.includes(hostname)) {
      console.warn(
        `[analytics] Hostname "${hostname}" is not mapped in GTM container GTM-NV9PKNM; ` +
        `no analytics will be sent. See Environment routing in ` +
        `src/app/core/services/analytics/CLAUDE.md`
      );
    }
  }
}
